import "./updateroomtype.css";
import { useState, useRef, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ToggleButton } from "src/Components/BasicComponent";
import API from "src/API2";
import { SERVE_URL } from "src/constants";
import toast from "react-hot-toast";

/* ── Inline icons ───────────────────────────────────────────────── */
const Svg = ({ children, size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{children}</svg>
);
const IcArrow  = () => <Svg><line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" /></Svg>;
const IcImage  = () => <Svg><rect x="3" y="3" width="18" height="18" rx="2" ry="2" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" /></Svg>;
const IcUpload = () => <Svg><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" /></Svg>;
const IcTag    = () => <Svg><path d="M20.59 13.41 13.42 20.58a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" /><line x1="7" y1="7" x2="7.01" y2="7" /></Svg>;
const IcUsers  = () => <Svg><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></Svg>;
const IcBed    = () => <Svg><path d="M2 20v-8a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v8" /><path d="M2 17h20" /><path d="M6 10V6a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v4" /></Svg>;
const IcCash   = () => <Svg><rect x="2" y="6" width="20" height="12" rx="2" /><circle cx="12" cy="12" r="3" /><path d="M6 12h.01M18 12h.01" /></Svg>;
const IcDoc    = () => <Svg><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></Svg>;
const IcCheck  = () => <Svg><polyline points="20 6 9 17 4 12" /></Svg>;
const IcClose  = () => <Svg size={14}><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></Svg>;
const IcKid    = () => <Svg size={14}><circle cx="12" cy="7" r="3" /><path d="M5 21v-2a4 4 0 0 1 4-4h6a4 4 0 0 1 4 4v2" /></Svg>;
const IcRuler  = () => <Svg size={14}><path d="M3 21l18-18" /><path d="M5 19l3-3M9 15l3-3M13 11l3-3" /></Svg>;
const IcGrid   = () => <Svg size={14}><rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /></Svg>;

const UpdateRoomType = () => {
  const ref = useRef();
  const navigate = useNavigate();
  const { roomId } = useParams();

  const [roomType, setRoomType] = useState("");
  const [nbAdulte, setNbAdulte] = useState("");
  const [nbChildren, setNbChildren] = useState("");
  const [possibleBedNumber, setPossibleBedNumber] = useState("");
  const [nbQueenBed, setNbQueenBed] = useState("");
  const [roomSize, setRoomSize] = useState("");
  const [availibility, setAvalibility] = useState(true);
  const [description, setDescription] = useState("");
  const [nbRooms, setNbRooms] = useState("");
  const [refundable, setRefundable] = useState(false);

  const [existingImages, setExistingImages] = useState([]); // server-stored, [{filename}, …]
  const [newImages, setNewImages] = useState(null);          // FileList from input
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  /* ── Load room ── */
  useEffect(() => {
    setLoading(true);
    API.get(`rooms/${roomId}`)
      .then((resp) => {
        const room = resp.data.room;
        setRoomType(room.code || "");
        setNbAdulte(room.max_adults ?? "");
        setNbChildren(room.max_children ?? "");
        setPossibleBedNumber(room.max_additional ?? "");
        setNbQueenBed(room.nb_queen ?? "");
        setRoomSize(room.size ?? "");
        setAvalibility(!!room.available);
        setDescription(room.description || "");
        setNbRooms(room.count ?? "");
        setExistingImages(Array.isArray(room.pictures) ? room.pictures : []);
        setRefundable(typeof room.refundable === "boolean" ? room.refundable : room.refundable === "true");
      })
      .catch((e) => {
        console.error(e);
        toast.error("Could not load room type");
      })
      .finally(() => setLoading(false));
  }, [roomId]);

  /* ── Submit ── */
  const postData = async () => {
    setSubmitting(true);
    const bodyFormData = new FormData();
    if (newImages) {
      Array.from(newImages).forEach((e) => bodyFormData.append("pictures", e));
    }
    bodyFormData.append("max_adults", nbAdulte);
    bodyFormData.append("max_children", nbChildren);
    bodyFormData.append("available", availibility);
    bodyFormData.append("size", roomSize);
    bodyFormData.append("nb_queen", nbQueenBed);
    bodyFormData.append("max_additional", possibleBedNumber);
    bodyFormData.append("count", nbRooms);
    bodyFormData.append("refundable", refundable);
    bodyFormData.append("description", description);

    toast.promise(
      API.put(`rooms/${roomId}`, bodyFormData).finally(() => setSubmitting(false)),
      {
        loading: "Updating room type…",
        success: "Room type updated",
        error: "Couldn't update this room type — check your info",
      },
    );
  };

  /* ── Image previews (server + new) ── */
  const previews = useMemo(() => {
    if (newImages && newImages.length) {
      return Array.from(newImages).map((f, i) => ({
        src: URL.createObjectURL(f),
        kind: "new",
        idx: i,
      }));
    }
    return (existingImages || []).map((p, i) => ({
      src: p?.filename
        ? `${SERVE_URL[process.env.NODE_ENV]}/media/rooms/${p.filename}`
        : "",
      kind: "existing",
      idx: i,
    }));
  }, [newImages, existingImages]);

  const removePreview = (kind, idx) => {
    if (kind === "new" && newImages) {
      const remaining = Array.from(newImages).filter((_, i) => i !== idx);
      if (remaining.length === 0) {
        setNewImages(null);
        return;
      }
      const dt = new DataTransfer();
      remaining.forEach((f) => dt.items.add(f));
      setNewImages(dt.files);
    } else if (kind === "existing") {
      setExistingImages((prev) => prev.filter((_, i) => i !== idx));
    }
  };

  /* ── Live preview values ── */
  const previewCode = roomType || "STD-01";
  const previewDesc = description || "Describe the comfort, view and amenities of this room.";
  const coverSrc = previews[0]?.src || "";

  return (
    <div className="crt-page">
      {/* ── Hero ── */}
      <header className="crt-hero">
        <button type="button" className="crt-back" onClick={() => navigate("/rooms")}>
          <IcArrow /> Back to room types
        </button>
        <div className="crt-hero__body">
          <div>
            <span className="crt-hero__eyebrow">Edit · {roomType || "Room type"}</span>
            <h1 className="crt-hero__title">Update room type</h1>
            <p className="crt-hero__sub">
              Adjust capacity, bedding, policies and pictures. The room code is locked
              once a room type has been created.
            </p>
          </div>
        </div>
      </header>

      <div className="crt-grid">
        {/* ── Form column ── */}
        <div className="crt-form">
          {/* Pictures */}
          <section className="crt-card">
            <header className="crt-card__head">
              <span className="crt-card__icon"><IcImage /></span>
              <div>
                <h3>Pictures</h3>
                <p>Replace the gallery — first picture is the cover.</p>
              </div>
            </header>

            <div
              className={`crt-dropzone ${previews.length ? "has-files" : ""}`}
              onClick={() => ref.current.click()}
            >
              {previews.length === 0 ? (
                <div className="crt-dropzone__empty">
                  <IcUpload />
                  <strong>Click to upload pictures</strong>
                  <span>Replaces all current images</span>
                </div>
              ) : (
                <div className="crt-thumbs">
                  {previews.map((p, i) => (
                    <div key={`${p.kind}-${p.idx}`} className={`crt-thumb ${i === 0 ? "is-cover" : ""}`}>
                      {p.src && <img src={p.src} alt={`Preview ${i + 1}`} />}
                      {i === 0 && <span className="crt-thumb__badge">Cover</span>}
                      <button
                        type="button"
                        className="crt-thumb__remove"
                        onClick={(e) => { e.stopPropagation(); removePreview(p.kind, p.idx); }}
                        aria-label="Remove picture"
                      >
                        <IcClose />
                      </button>
                    </div>
                  ))}
                  <div className="crt-thumb crt-thumb--add">
                    <IcUpload />
                    <span>Replace gallery</span>
                  </div>
                </div>
              )}
            </div>
            <input
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              multiple
              ref={ref}
              onChange={(e) => setNewImages(e.target.files)}
            />
            {newImages && newImages.length > 0 && (
              <span className="crt-help">
                {newImages.length} new picture{newImages.length > 1 ? "s" : ""} ready — they will replace the current gallery on save.
              </span>
            )}
          </section>

          {/* Identity */}
          <section className="crt-card">
            <header className="crt-card__head">
              <span className="crt-card__icon crt-card__icon--orange"><IcTag /></span>
              <div>
                <h3>Identity</h3>
                <p>Room code is locked. Adjust how many rooms of this type exist.</p>
              </div>
            </header>
            <div className="crt-grid-2">
              <label className="crt-field">
                <span>Room code</span>
                <input value={roomType} disabled />
                <span className="crt-help">Cannot be changed once the type is created.</span>
              </label>
              <label className="crt-field">
                <span>Number of rooms</span>
                <input
                  type="number"
                  min={1}
                  placeholder="How many rooms of this type"
                  value={nbRooms}
                  onChange={(e) => setNbRooms(e.target.value)}
                />
              </label>
            </div>
          </section>

          {/* Capacity & beds */}
          <section className="crt-card">
            <header className="crt-card__head">
              <span className="crt-card__icon crt-card__icon--blue"><IcUsers /></span>
              <div>
                <h3>Capacity & bedding</h3>
                <p>Maximum guests and how the room is set up.</p>
              </div>
            </header>
            <div className="crt-grid-2">
              <label className="crt-field">
                <span>Max adults</span>
                <input
                  type="number"
                  min={1}
                  value={nbAdulte}
                  onChange={(e) => setNbAdulte(e.target.value)}
                />
              </label>
              <label className="crt-field">
                <span>Max children</span>
                <input
                  type="number"
                  min={0}
                  value={nbChildren}
                  onChange={(e) => setNbChildren(e.target.value)}
                />
              </label>
              <label className="crt-field">
                <span>Queen beds</span>
                <input
                  type="number"
                  min={0}
                  value={nbQueenBed}
                  onChange={(e) => setNbQueenBed(e.target.value)}
                />
              </label>
              <label className="crt-field">
                <span>Additional beds available</span>
                <input
                  type="number"
                  min={0}
                  value={possibleBedNumber}
                  onChange={(e) => setPossibleBedNumber(e.target.value)}
                />
              </label>
              <label className="crt-field">
                <span>Room size (m²)</span>
                <input
                  type="number"
                  min={1}
                  value={roomSize}
                  onChange={(e) => setRoomSize(e.target.value)}
                />
              </label>
            </div>
          </section>

          {/* Policies */}
          <section className="crt-card">
            <header className="crt-card__head">
              <span className="crt-card__icon crt-card__icon--green"><IcCash /></span>
              <div>
                <h3>Policies</h3>
                <p>Refunds and live availability.</p>
              </div>
            </header>
            <div className="crt-grid-2">
              <label className="crt-field">
                <span>Refundable</span>
                <select
                  value={String(refundable)}
                  onChange={(e) => setRefundable(e.target.value === "true")}
                >
                  <option value="false">No — non-refundable</option>
                  <option value="true">Yes — fully refundable</option>
                </select>
              </label>
              <div className="crt-field">
                <span>Availability</span>
                <div className="crt-toggle-row">
                  <ToggleButton state={availibility} setVal={setAvalibility} />
                  <span className={`crt-status crt-status--${availibility ? "on" : "off"}`}>
                    {availibility ? "Available now" : "Off market"}
                  </span>
                </div>
              </div>
            </div>
          </section>

          {/* Description */}
          <section className="crt-card">
            <header className="crt-card__head">
              <span className="crt-card__icon crt-card__icon--orange"><IcDoc /></span>
              <div>
                <h3>Description</h3>
                <p>Shown to guests on the booking site.</p>
              </div>
            </header>
            <label className="crt-field">
              <span>Description</span>
              <textarea
                rows={5}
                placeholder="Describe the room — view, comfort, amenities…"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
              <span className="crt-counter">{description.length} characters</span>
            </label>
          </section>
        </div>

        {/* ── Live preview ── */}
        <aside className="crt-summary">
          <div className="crt-summary__card">
            <span className="crt-summary__eyebrow">Live preview</span>
            <h3>How it will look</h3>

            <div className="crt-preview">
              {coverSrc ? (
                <img src={coverSrc} alt="" className="crt-preview__cover" />
              ) : (
                <div className="crt-preview__cover crt-preview__cover--empty">
                  <IcImage />
                  <span>No cover picture</span>
                </div>
              )}
              <div className="crt-preview__body">
                <div className="crt-preview__head">
                  <span className="crt-preview__code">{previewCode}</span>
                  <span className={`crt-status crt-status--${availibility ? "on" : "off"}`}>
                    {availibility ? "Available" : "Off market"}
                  </span>
                </div>
                <p className="crt-preview__desc">{previewDesc}</p>
                <div className="crt-preview__stats">
                  <div className="crt-pstat">
                    <IcUsers />
                    <strong>{nbAdulte || 0}</strong>
                    <span>Adults</span>
                  </div>
                  <div className="crt-pstat">
                    <IcKid />
                    <strong>{nbChildren || 0}</strong>
                    <span>Children</span>
                  </div>
                  <div className="crt-pstat">
                    <IcGrid />
                    <strong>{nbRooms || 0}</strong>
                    <span>Rooms</span>
                  </div>
                  <div className="crt-pstat">
                    <IcRuler />
                    <strong>{roomSize || 0}m²</strong>
                    <span>Size</span>
                  </div>
                </div>
                <div className="crt-preview__tags">
                  <span className={`crt-tag ${refundable ? "crt-tag--green" : "crt-tag--gray"}`}>
                    {refundable ? "Refundable" : "Non-refundable"}
                  </span>
                  <span className="crt-tag crt-tag--blue">
                    <IcBed /> {nbQueenBed || 0} queen
                  </span>
                  {Number(possibleBedNumber) > 0 && (
                    <span className="crt-tag crt-tag--gray">
                      +{possibleBedNumber} extra bed{Number(possibleBedNumber) > 1 ? "s" : ""}
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="crt-summary__actions">
              <button
                type="button"
                className="crt-btn crt-btn--ghost"
                onClick={() => (window.location = "/rooms")}
              >
                Cancel
              </button>
              <button
                type="button"
                className="crt-btn crt-btn--primary"
                onClick={() => !submitting && postData()}
                disabled={submitting || loading}
              >
                {submitting ? "Saving…" : (<><IcCheck /> Save changes</>)}
              </button>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default UpdateRoomType;
